"use client";

import { useEffect, useState } from "react";
import { notifyBugsnag } from "@/lib/bugsnag";
import { recoverFromChunkError } from "@/lib/chunk-reload";

/**
 * Last-resort boundary. Without it, anything that escapes the route
 * boundaries renders Next's built-in "Application error: a client-side
 * exception has occurred" — a dead end that reports nowhere. The Electron
 * shell can't see these either: a renderer-side throw fires none of
 * `did-fail-load` / `render-process-gone` / `unresponsive`, so from the main
 * process nothing crashed and Bugsnag got nothing.
 *
 * This file replaces the root layout when it renders, so ErrorReporter isn't
 * mounted and the report has to be made here directly. Styling is inline for
 * the same reason — a stale deployment may be exactly why we're here, and the
 * CSS chunk could 404 too.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // A chunk failure means the window is pinned to a deployment that no longer
  // exists; reloading lands on the current build. Suppress the UI while the
  // reload is in flight so the error screen doesn't flash.
  const [recovering] = useState(() => recoverFromChunkError(error));

  useEffect(() => {
    notifyBugsnag(error, {
      context: "global-error-boundary",
      severity: "error",
      metadata: {
        report: {
          url: window.location.href,
          digest: error.digest,
          recoveringFromChunkError: recovering,
        },
      },
    });
  }, [error, recovering]);

  return (
    <html lang="en">
      {/* System colours follow the shell's native theme; without a background
          the macOS app's translucent window would show through. */}
      <body style={{ margin: 0, fontFamily: "system-ui, sans-serif", colorScheme: "light dark", background: "Canvas", color: "CanvasText", cursor: "default" }}>
        {/* The desktop app has no title bar; keep the window movable. A no-op
            in browsers. Inline because this page can't rely on app CSS. */}
        <div aria-hidden style={{ position: "fixed", top: 0, left: 0, right: 0, height: 38, WebkitAppRegion: "drag" } as React.CSSProperties} />
        {!recovering && (
          <div
            style={{
              display: "flex",
              minHeight: "100vh",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 16,
              padding: 32,
              textAlign: "center",
            }}
          >
            <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Something went wrong</h1>
            <p style={{ fontSize: 14, opacity: 0.7, margin: 0, maxWidth: 420 }}>
              Spoke Chat hit an unexpected error. Reloading usually fixes it.
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: "8px 16px",
                  fontSize: 14,
                  borderRadius: 6,
                  border: "1px solid currentColor",
                  background: "transparent",
                  cursor: "default",
                }}
              >
                Reload
              </button>
              <button
                onClick={reset}
                style={{
                  padding: "8px 16px",
                  fontSize: 14,
                  borderRadius: 6,
                  border: "1px solid transparent",
                  background: "#111",
                  color: "#fff",
                  cursor: "default",
                }}
              >
                Try again
              </button>
            </div>
          </div>
        )}
      </body>
    </html>
  );
}
